import { inject, injectable, injectFromBase } from 'inversify'
import DomainBaseService from '@/services/domain/domainBaseService'
import { DomainSettings } from '@/decorators/domainDecorators'
import PhoneNumber from '@/data/models/domain/PhoneNumber'
import type PhoneNumbersRepository from '@/data/repositories/domain/phoneNumbersRepository'
import type { BuyNumberBody, ChangeForwardingBody } from '@/data/repositories/domain/phoneNumbersRepository'
import type { DedicatedNumbersQueryParams } from '@/services/domain/phoneNumbers/types'
import { ModelEventType } from '@/services/transport/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type EntityManagerService from '@/data/repositories/entityManagerService'
import type ModelSubscriptionService from '@/services/transport/modelSubscriptionService'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import type UserService from '@/services/domain/user/userService'
import type { PaginationParams } from '@/services/tables/pagination/types'
import type { PaginationUrlType } from '@/services/tables/types'
import { ForwardCallsToVariant } from '@/services/domain/phoneNumbers/types'
import type TeamWorkflowService from '@/services/domain/accounts/teamWorkflowService'
import type UserSettingsService from '@/services/domain/user/userSettingsService'
import type UserNumbersPreloaderService from '@/services/preloaders/userNumbersPreloaderService'
import { isDev, isStaging } from '@/utils/system'
import TmLogicError from '@/core/error/tmLogicError'
import type { MonitoringServiceInterface } from '@/services/monitoring/types'

@DomainSettings({
  model: PhoneNumber,
})
@injectable()
@injectFromBase()
export default class PhoneNumbersService extends DomainBaseService<PhoneNumbersRepository> {
  constructor(
    @inject(SERVICE_TYPES.EntityManager) protected readonly entityManager: EntityManagerService,
    @inject(SERVICE_TYPES.ModelSubscriptionService) protected readonly subscription: ModelSubscriptionService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
    @inject(SERVICE_TYPES.UserService) protected readonly userService: UserService,
    @inject(SERVICE_TYPES.TeamWorkflowService) protected readonly teamWorkflowService: TeamWorkflowService,
    @inject(SERVICE_TYPES.UserSettingsService) protected readonly userSettingsService: UserSettingsService,
    @inject(SERVICE_TYPES.UserNumbersPreloaderService)
    protected readonly userNumbersPreloaderService: UserNumbersPreloaderService,
    @inject(SERVICE_TYPES.MonitoringService) protected readonly monitoringService: MonitoringServiceInterface,
  ) {
    super(entityManager, subscription, preloaderManager)
  }

  public isAllowBuyCaLocalNumber() {
    return false
  }

  public async updatePhoneLabel(id: string, label: string) {
    const repository = this.getDomainRepository()
    await repository.updatePhoneLabel(id, label)
    this.notify([id], ModelEventType.UPDATE)
  }

  public async buyNumber(body: BuyNumberBody) {
    const result = await this.getDomainRepository().buyNumber(body)
    this.userNumbersPreloaderService.markAsNotPreloaded()
    return result
  }

  public getNumberById(id: string) {
    return this.getDomainRepository().getNumberById(id)
  }

  public getNumberByPhone(phone: string) {
    return this.getDomainRepository().getNumberByPhone(phone)
  }

  public fill(id: string) {
    return this.getDomainRepository().fill(id)
  }

  public findEntityWithAll(id: string) {
    return this.getDomainRepository().query().withAll().find(id)
  }

  public async changeForwardingNumber(body: ChangeForwardingBody) {
    await this.getDomainRepository().changeForwardingNumber(body)
    this.notify([body.numberId], ModelEventType.UPDATE)
  }

  public async fetchPhoneNumbersByUserId(userId: string) {
    const params: DedicatedNumbersQueryParams = {
      userId,
    }

    const pagination: PaginationParams = {
      requestParams: {
        page: 1,
        perPage: 1e3,
      },
      pageKey: '',
    }

    const { items } = await this.getDomainRepository().fetchPhoneNumbers(params, pagination)
    return items
  }

  public async getVoiceableNumbers() {
    const currentUser = this.userService.currentUser()
    const parentId = this.userSettingsService.currentUserSettingsOrFail().parentId
    const userId = this.teamWorkflowService.isUsersWorkflow() ? parentId : currentUser.id

    const page = 1
    const perPage = 1000

    const res = await this.getNumbersByFilter(
      {
        filter: [],
        other: {
          userId,
          voiceable: true,
        },
      },
      {
        pageKey: `${page}-${perPage}`,
        requestParams: {
          page,
          perPage,
        },
      },
    )

    if (res.pagination.totalCount > res.pagination.perPage) {
      const error = new TmLogicError(
        `Voiceable numbers do not fit into a single page. totalCount = ${res.pagination.totalCount}, perPage = ${res.pagination.perPage}`,
      )
      this.monitoringService.logError(error)
      if (isDev || isStaging) {
        throw error
      }
    }

    return res
  }

  public getNumbersByFilter(queryParameterBag: PaginationUrlType, paginationParamsBag?: PaginationParams) {
    return this.getDomainRepository().gridRequest(queryParameterBag, paginationParamsBag)
  }

  public isAllowSetUpCallForwarding(phoneNumber: Pick<PhoneNumber, 'voiceAvailable' | 'forwardCallsTo'>) {
    return phoneNumber.voiceAvailable && phoneNumber.forwardCallsTo !== ForwardCallsToVariant.notSupported
  }

  public async restoreNumber(id: string) {
    await this.getDomainRepository().restoreNumber(id)
    this.userNumbersPreloaderService.markAsNotPreloaded()
    this.notify([id], ModelEventType.UPDATE)
  }

  public async cancelNumber(id: string) {
    await this.getDomainRepository().cancelNumber(id)
    this.userNumbersPreloaderService.markAsNotPreloaded()
    this.notify([id], ModelEventType.UPDATE)
  }
}
